/**
 * NetworkMap
 *
 * CSS flex layout visualization showing site connectivity chain:
 * SITE → EMS SERVER → VPN TUNNEL → CLOUD
 *
 * Uses CSS instead of SVG for simplicity (per implementation plan decision).
 */

import { useConnectivity } from '../../hooks/useSecurity';
import { Network, Loader2, Server, Cloud, Shield, Radio } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const STATUS_COLORS = {
  up: 'bg-ems-green',
  established: 'bg-ems-green',
  connected: 'bg-ems-green',
  degraded: 'bg-ems-amber',
  down: 'bg-ems-red',
  offline: 'bg-ems-red',
  unknown: 'bg-ems-text-muted',
};

function getStatusColor(status: string): string {
  return STATUS_COLORS[status as keyof typeof STATUS_COLORS] || STATUS_COLORS.unknown;
}

function ConnectorLine({ status }: { status: string }) {
  const color = status === 'up' || status === 'established' || status === 'connected'
    ? 'bg-ems-green/40'
    : status === 'degraded'
    ? 'bg-ems-amber/40'
    : 'bg-ems-red/40';

  return <div className={`flex-1 h-0.5 ${color} mx-1`} />;
}

export function NetworkMap() {
  const { data, isLoading } = useConnectivity();
  const navigate = useNavigate();

  return (
    <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-5">
      <div className="flex items-center gap-2 mb-4">
        <Network className="w-4 h-4 text-ems-text-muted" />
        <h2 className="text-sm font-semibold text-ems-text-primary uppercase tracking-wider">
          Network Connectivity Map
        </h2>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-ems-text-muted" />
        </div>
      ) : !data?.connectivity?.length ? (
        <div className="py-8 text-center text-sm text-ems-text-muted">
          No connectivity data available
        </div>
      ) : (
        <div className="space-y-2">
          {/* Header row */}
          <div className="flex items-center px-3 py-1">
            <div className="w-32 text-[10px] uppercase tracking-wider text-ems-text-muted font-semibold">Site</div>
            <div className="flex-1 flex items-center justify-between px-4">
              <span className="text-[10px] uppercase tracking-wider text-ems-text-muted font-semibold">EMS</span>
              <span className="text-[10px] uppercase tracking-wider text-ems-text-muted font-semibold">VPN</span>
              <span className="text-[10px] uppercase tracking-wider text-ems-text-muted font-semibold">Cloud</span>
            </div>
          </div>

          {/* Site rows */}
          {data.connectivity.map((site) => (
            <div
              key={site.site_id}
              className="flex items-center px-3 py-2 rounded-lg hover:bg-ems-bg-card-hover cursor-pointer transition-colors"
              onClick={() => navigate(`/security/site/${site.site_id}`)}
            >
              {/* Site name */}
              <div className="w-32 truncate">
                <p className="text-xs font-medium text-ems-text-primary truncate">{site.site_name}</p>
                <p className="text-[10px] text-ems-text-muted font-mono">{site.site_code}</p>
              </div>

              {/* Connection chain */}
              <div className="flex-1 flex items-center px-2">
                {/* Site node */}
                <div className="flex flex-col items-center gap-0.5">
                  <Radio className="w-3.5 h-3.5 text-ems-text-muted" />
                  <div className={`w-2 h-2 rounded-full ${getStatusColor(site.connection_status)}`} />
                </div>

                <ConnectorLine status={site.connection_status} />

                {/* EMS Server node */}
                <div className="flex flex-col items-center gap-0.5">
                  <Server className="w-3.5 h-3.5 text-ems-text-muted" />
                  <div className={`w-2 h-2 rounded-full ${getStatusColor(site.connection_status)}`} />
                </div>

                <ConnectorLine status={site.tunnel_status} />

                {/* VPN Tunnel node */}
                <div className="flex flex-col items-center gap-0.5">
                  <Shield className="w-3.5 h-3.5 text-ems-text-muted" />
                  <div className={`w-2 h-2 rounded-full ${getStatusColor(site.tunnel_status)}`} />
                </div>

                <ConnectorLine status={site.tunnel_status} />

                {/* Cloud node */}
                <div className="flex flex-col items-center gap-0.5">
                  <Cloud className="w-3.5 h-3.5 text-ems-text-muted" />
                  <div className={`w-2 h-2 rounded-full ${getStatusColor(site.tunnel_status === 'up' ? 'connected' : site.tunnel_status)}`} />
                </div>
              </div>

              {/* Status badge */}
              <div className="w-20 text-right">
                <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${
                  site.tunnel_status === 'up'
                    ? 'bg-ems-green/10 text-ems-green'
                    : site.tunnel_status === 'degraded'
                    ? 'bg-ems-amber/10 text-ems-amber'
                    : 'bg-ems-red/10 text-ems-red'
                }`}>
                  {site.tunnel_status === 'up' ? 'Online' : site.tunnel_status === 'degraded' ? 'Degraded' : 'Offline'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
